/**
 * ChatColumn - Dark theme middle panel for Q&A with citations
 *
 * Features:
 * - Dark glass morphism design
 * - Question input with source count indicator
 * - AI responses with inline citation markers
 * - Save to Note / feedback buttons
 * - Loading states
 */

import { useState, useRef, useEffect } from 'react';
import { KnowledgeCitation, KnowledgeCitationSupport } from '@deep-agents/shared-types';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';

// Icons
const SettingsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="4" y1="21" x2="4" y2="14" />
    <line x1="4" y1="10" x2="4" y2="3" />
    <line x1="12" y1="21" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12" y2="3" />
    <line x1="20" y1="21" x2="20" y2="16" />
    <line x1="20" y1="12" x2="20" y2="3" />
    <line x1="1" y1="14" x2="7" y2="14" />
    <line x1="9" y1="8" x2="15" y2="8" />
    <line x1="17" y1="16" x2="23" y2="16" />
  </svg>
);

const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="1" />
    <circle cx="19" cy="12" r="1" />
    <circle cx="5" cy="12" r="1" />
  </svg>
);

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: KnowledgeCitation[];
  citationSupports?: KnowledgeCitationSupport[];
  timestamp: Date;
}

interface ChatColumnProps {
  spaceId: string;
  selectedDocumentIds: string[];
  onCitationClick?: (documentId: string) => void;
  /** Optional callback to save a message as a note */
  onSaveAsNote?: (content: string) => void;
}

export function ChatColumn({
  spaceId,
  selectedDocumentIds,
  onCitationClick,
  onSaveAsNote,
}: ChatColumnProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle sending a question
  const handleSendMessage = async (question: string) => {
    if (!question.trim()) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: question,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/kb/spaces/${spaceId}/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          question,
          selectedDocumentIds: selectedDocumentIds.length > 0 ? selectedDocumentIds : undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to get answer');
      }

      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data?.data?.answer || 'No answer found.',
        citations: data?.data?.citations || [],
        citationSupports: data?.data?.citationSupports || undefined,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get answer';
      setError(errorMessage);

      const errorAssistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorAssistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = () => {
    setMessages([]);
    setError(null);
  };

  return (
    <div className="chat-column">
      {/* Header */}
      <div className="chat-header">
        <h2 className="chat-title">Chat</h2>
        <div className="header-actions">
          <button type="button" className="header-btn" title="Settings">
            <SettingsIcon />
          </button>
          <button type="button" className="header-btn" title="More options">
            <MoreIcon />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <p className="empty-title">Ask questions about your sources</p>
            <p className="empty-hint">
              Select sources on the left, then ask questions to get answers with citations.
            </p>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                onCitationClick={onCitationClick}
                onSaveAsNote={onSaveAsNote}
              />
            ))}
            {isLoading && (
              <div className="loading-message">
                <div className="loading-avatar" />
                <div className="loading-content">
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <ChatInput
        onSend={handleSendMessage}
        isLoading={isLoading}
        placeholder="Ask a question..."
        sourceCount={selectedDocumentIds.length}
      />

      <style>{`
        .chat-column {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: transparent;
        }

        .chat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          flex-shrink: 0;
        }

        .chat-title {
          font-size: 14px;
          font-weight: 600;
          color: #fff;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 4px;
        }

        .header-btn {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          border-radius: 6px;
          color: rgba(148, 163, 184, 0.5);
          cursor: pointer;
          transition: all 0.15s;
        }

        .header-btn:hover {
          background: rgba(255, 255, 255, 0.05);
          color: rgba(148, 163, 184, 0.8);
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px 20px;
        }

        /* Scrollbar styling */
        .chat-messages::-webkit-scrollbar {
          width: 5px;
        }
        .chat-messages::-webkit-scrollbar-track {
          background: transparent;
        }
        .chat-messages::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.08);
          border-radius: 3px;
        }
        .chat-messages::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.15);
        }

        .chat-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
          padding: 32px 20px;
        }

        .empty-icon {
          color: rgba(99, 102, 241, 0.25);
          margin-bottom: 12px;
        }

        .empty-title {
          font-size: 14px;
          font-weight: 500;
          color: rgba(226, 232, 240, 0.7);
          margin: 0 0 6px;
        }

        .empty-hint {
          font-size: 12px;
          color: rgba(100, 116, 139, 0.5);
          margin: 0;
          max-width: 260px;
          line-height: 1.5;
        }

        .loading-message {
          display: flex;
          gap: 10px;
          padding: 12px 0;
        }

        .loading-avatar {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(99, 102, 241, 0.15);
          flex-shrink: 0;
        }

        .loading-content {
          padding: 10px 14px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 10px;
        }

        .loading-dots {
          display: flex;
          gap: 4px;
        }

        .loading-dots span {
          width: 6px;
          height: 6px;
          background: rgba(99, 102, 241, 0.4);
          border-radius: 50%;
          animation: bounce 1.4s ease-in-out infinite;
        }

        .loading-dots span:nth-child(2) {
          animation-delay: 0.2s;
        }

        .loading-dots span:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes bounce {
          0%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-5px);
          }
        }
      `}</style>
    </div>
  );
}
